/**
 * Modal: crop image before upload (react-easy-crop).
 * Tammy picks a shape (square for cards, portrait, original, etc.).
 */

import { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { HiX } from 'react-icons/hi';
import { getCroppedImg } from '../utils/imageCrop';
import {
  CROP_ASPECT_PRESETS,
  cropAspectValue,
  cropPresetIdForAspect,
  type CropAspectId,
} from '../utils/cropAspectPresets';

interface Props {
  imageSrc: string;
  aspect: number;
  /** Original file when user skips crop — upload as-is */
  originalFile: File;
  onClose: () => void;
  onConfirm: (blob: Blob) => void;
  onSkipUpload: (file: File) => void;
}

export default function ImageSelectorCropModal({
  imageSrc,
  aspect,
  originalFile,
  onClose,
  onConfirm,
  onSkipUpload,
}: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspectId, setAspectId] = useState<CropAspectId>(() => cropPresetIdForAspect(aspect));
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const cropAspect = cropAspectValue(aspectId);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onConfirm(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Crop before upload</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <HiX className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1 mb-2" role="group" aria-label="Crop shape">
          {CROP_ASPECT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              aria-pressed={aspectId === preset.id}
              onClick={() => setAspectId(preset.id)}
              className={`text-xs px-2 py-1 rounded border ${
                aspectId === preset.id
                  ? 'border-purple-500 bg-purple-50 text-purple-800'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="relative w-full aspect-square bg-gray-900 rounded-lg overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={cropAspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <label className="text-sm text-gray-600">Zoom</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => onSkipUpload(originalFile)}
            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
          >
            Skip crop
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !croppedAreaPixels}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-50"
          >
            {busy ? 'Uploading…' : 'Crop & upload'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Skip crop keeps the whole file. Square is best for menu cards. Original / Portrait keep tall cake photos.
        </p>
      </div>
    </div>
  );
}
